import { z } from 'zod';

import { TypeCaseStudy } from '../models/contentful-generated';
import { ContentfulAssetSchema } from './contentful-asset.validator';
import { ContentfulSysSchema } from './contentful-sys.validator';
import { CountryEntrySchema } from './country.validator';
import { FeatureEntrySchema } from './feature.validator';
import { ProcessEntrySchema } from './process.validator';
import { ProductEntrySchema } from './product.validator';
import { SegmentEntrySchema } from './segment.validator';
import { SeoMetadataEntrySchema } from './seo-metadata.validator';

const CaseStudyFieldsSchema: z.ZodType<any> = z.lazy(() =>
  z.looseObject({
    title: z.string(),
    slug: z.string(),
    images: z.array(ContentfulAssetSchema).optional().nullable(),
    productionCountry: CountryEntrySchema.optional().nullable(),
    link: z.string().optional().nullable(),
    products: z.array(ProductEntrySchema).optional().nullable(),
    functionKeyRequirements: z.string().optional().nullable(),
    benefits: z.string().optional().nullable(),
    processingMethod: ProcessEntrySchema.optional().nullable(),
    features: z.array(FeatureEntrySchema).optional().nullable(),
    relatedCaseStudies: z.array(CaseStudyEntrySchema).optional().nullable(),
    seoMetadata: SeoMetadataEntrySchema.optional().nullable(),
    segments: z.array(SegmentEntrySchema).optional().nullable(),
    confidential: z.boolean().optional().nullable(),
    visibility: z.string().optional().nullable(),
    type: z.string().optional().nullable(),
    excerpt: z.string().optional().nullable()
  })
);

export const CaseStudyEntrySchema = z.object({
  sys: ContentfulSysSchema,
  fields: CaseStudyFieldsSchema,
  metadata: z
    .object({
      tags: z.array(z.any()).optional().nullable()
    })
    .optional()
    .nullable()
});

export function validateCaseStudyEntry(data: TypeCaseStudy) {
  return CaseStudyEntrySchema.safeParse(data);
}

/**
 * Returns validation result.
 *
 * @param data
 */
export function validateCaseStudyEntries(data: TypeCaseStudy[]) {
  return z.array(CaseStudyEntrySchema).safeParse(data);
}

/**
 * Returns valid entries only. In most cases, this is preferred,
 * It allows us to still show some entries instead of showing nothing when only a single entry is invalid.
 *
 * @param data
 */
export function validateAndFilterCaseStudyEntries(data: TypeCaseStudy[]) {
  if (!data) return [];

  return data.reduce((acc: TypeCaseStudy[], entry) => {
    const validationResult = validateCaseStudyEntry(entry);
    if (validationResult.success) {
      acc.push(validationResult.data as unknown as TypeCaseStudy);
    } else {
      console.warn(
        `Validation failed for case study entry with id: '${entry?.sys?.id}'. It will be filtered out. Error: ${validationResult.error.message}`
      );
    }
    return acc;
  }, []);
}
